import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {
  MdDownload,
  MdDescription,
  MdImage,
  MdInsertDriveFile,
  MdOpenInNew,
  MdZoomIn,
  MdZoomOut,
  MdZoomOutMap,
} from 'react-icons/md';
import { OutlineButton } from './Buttons';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

function resolveKind(file) {
  if (!file) return 'file';
  if (file.kind === 'pdf' || file.kind === 'image') return file.kind;
  const mime = String(file.mimeType || '').toLowerCase();
  if (mime.includes('pdf')) return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  const name = String(file.fileName || file.name || file.url || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  return 'file';
}

/**
 * Shared dashboard file viewer for images + PDFs.
 * ~75% viewport with equal outer margin, scroll, zoom, download, close.
 *
 * file: { url, label?, fileName?, kind?: 'image'|'pdf', mimeType? }
 */
export function FileViewerModal({ isOpen, onClose, file = null }) {
  const [zoom, setZoom] = useState(1);

  const kind = useMemo(() => resolveKind(file), [file]);
  const title = file?.label || file?.fileName || 'File preview';
  const url = file?.url || null;
  const fileName = file?.fileName || file?.name || 'download';

  useEffect(() => {
    if (isOpen) setZoom(1);
  }, [isOpen, file?.url, file?.id]);

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  const handleDownload = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const KindIcon = kind === 'pdf' ? MdDescription : kind === 'image' ? MdImage : MdInsertDriveFile;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      // Avoid size="full" — it pins the dialog to the viewport edges.
    >
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg="bg.surface"
        borderRadius="md"
        // Equal breathing room on all sides (like left/right margin)
        mx={{ base: 3, md: 8 }}
        my={{ base: 4, md: 8 }}
        w={{ base: 'calc(100vw - 24px)', md: '75vw' }}
        maxW="75vw"
        h={{ base: 'calc(100vh - 32px)', md: '75vh' }}
        maxH={{ base: 'calc(100vh - 32px)', md: '75vh' }}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader
          flexShrink={0}
          borderBottomWidth="1px"
          borderColor="border.subtle"
          py={3}
          pr={12}
        >
          <Flex align="center" justify="space-between" gap={3} wrap="wrap">
            <HStack spacing={3} minW={0} flex={1}>
              <Icon as={KindIcon} boxSize={5} color="text.secondary" flexShrink={0} />
              <Box minW={0}>
                <Heading size="sm" noOfLines={1}>
                  {title}
                </Heading>
                {fileName && fileName !== title ? (
                  <Text fontSize="12px" color="text.secondary" fontFamily="mono" noOfLines={1}>
                    {fileName}
                  </Text>
                ) : null}
              </Box>
            </HStack>
            <HStack spacing={1} flexShrink={0} pr={2}>
              <Tooltip label="Zoom out">
                <IconButton
                  aria-label="Zoom out"
                  icon={<MdZoomOut />}
                  size="sm"
                  variant="ghost"
                  onClick={zoomOut}
                  isDisabled={zoom <= MIN_ZOOM}
                />
              </Tooltip>
              <Text
                fontSize="12px"
                fontFamily="mono"
                minW="44px"
                textAlign="center"
                color="text.secondary"
              >
                {Math.round(zoom * 100)}%
              </Text>
              <Tooltip label="Zoom in">
                <IconButton
                  aria-label="Zoom in"
                  icon={<MdZoomIn />}
                  size="sm"
                  variant="ghost"
                  onClick={zoomIn}
                  isDisabled={zoom >= MAX_ZOOM}
                />
              </Tooltip>
              <Tooltip label="Reset zoom">
                <IconButton
                  aria-label="Reset zoom"
                  icon={<MdZoomOutMap />}
                  size="sm"
                  variant="ghost"
                  onClick={zoomReset}
                />
              </Tooltip>
              {url ? (
                <>
                  <Tooltip label="Download">
                    <IconButton
                      aria-label="Download"
                      icon={<MdDownload />}
                      size="sm"
                      variant="ghost"
                      onClick={handleDownload}
                    />
                  </Tooltip>
                  <Tooltip label="Open in new tab">
                    <IconButton
                      aria-label="Open in new tab"
                      icon={<MdOpenInNew />}
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                    />
                  </Tooltip>
                </>
              ) : null}
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" minH={0} p={0} overflow="hidden">
          {!url ? (
            <Flex h="100%" align="center" justify="center" p={8}>
              <Text color="text.secondary">No file URL available to preview.</Text>
            </Flex>
          ) : kind === 'image' ? (
            <Box h="100%" overflow="auto" bg="bg.input" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
              <Flex minH="100%" align="center" justify="center">
                <Image
                  src={url}
                  alt={title}
                  maxW={{ base: '100%', md: '90%' }}
                  maxH={{ base: 'calc(100vh - 220px)', md: 'calc(75vh - 160px)' }}
                  objectFit="contain"
                  transform={`scale(${zoom})`}
                  transformOrigin="center center"
                  transition="transform 0.15s ease"
                  borderRadius="sm"
                  boxShadow="sm"
                />
              </Flex>
            </Box>
          ) : kind === 'pdf' ? (
            <Box h="100%" overflow="auto" bg="bg.input" px={{ base: 3, md: 4 }} py={{ base: 4, md: 5 }}>
              <Box
                w="100%"
                h={`${Math.max(100, zoom * 100)}%`}
                minH="100%"
                transform={`scale(${zoom})`}
                transformOrigin="top center"
                transition="transform 0.15s ease"
                borderRadius="sm"
                overflow="hidden"
                bg="white"
              >
                <Box
                  as="iframe"
                  title={title}
                  src={url}
                  w="100%"
                  h="100%"
                  minH={{ base: 'calc(100vh - 200px)', md: 'calc(75vh - 140px)' }}
                  border="0"
                />
              </Box>
            </Box>
          ) : (
            <Flex h="100%" direction="column" align="center" justify="center" gap={4} p={8}>
              <Icon as={MdInsertDriveFile} boxSize={12} color="text.secondary" />
              <Text color="text.secondary" textAlign="center">
                Preview not available for this file type. Download or open in a new tab.
              </Text>
              <HStack>
                <OutlineButton leftIcon={<MdDownload />} onClick={handleDownload}>
                  Download
                </OutlineButton>
                <OutlineButton
                  leftIcon={<MdOpenInNew />}
                  onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                >
                  Open
                </OutlineButton>
              </HStack>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
